import { ManagedAgentResponseModel } from '@/customApiClients/mgmtApiClient/Api';
import { getFileExtension } from '@/lib/fileType';
import { cn } from '@/lib/utils';
import {
    Brain,
    FileArchive,
    File as FileIcon,
    FileImage,
    FileJson,
    FileSpreadsheet,
    FileText,
    Loader2,
    LucideIcon,
    Plus
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { Switch } from '@/components/ui/switch';

import { ManagedAgentPicker } from './ManagedAgentPicker';

interface ReferenceFileItem {
    id: string; // mgmt ClaudeFile row guid
    anthropicFileId: string;
    filename: string;
}

const REFERENCE_FILE_ICONS: Record<string, LucideIcon> = {
    png: FileImage,
    jpg: FileImage,
    jpeg: FileImage,
    gif: FileImage,
    svg: FileImage,
    webp: FileImage,
    xlsx: FileSpreadsheet,
    xls: FileSpreadsheet,
    xlsm: FileSpreadsheet,
    xlsb: FileSpreadsheet,
    csv: FileSpreadsheet,
    json: FileJson,
    zip: FileArchive,
    rar: FileArchive,
    pdf: FileText,
    docx: FileText,
    doc: FileText,
    txt: FileText,
    md: FileText
};

const getReferenceFileIcon = (filename: string): LucideIcon =>
    REFERENCE_FILE_ICONS[getFileExtension(filename) ?? ''] ?? FileIcon;

interface StepContextProps {
    managedAgents: ManagedAgentResponseModel[];
    managedAgentIds: string[];
    setManagedAgentIds: (v: string[]) => void;
    // Persistent memory is a plain yes/no here — the actual memory store and
    // its fixed memory file are created behind the scenes at publish time
    // (named after the job), not picked/named by the user. See
    // ScheduledAgentSetup.tsx / ManageAgent.tsx's save handlers.
    useMemory: boolean;
    setUseMemory: (v: boolean) => void;
    selectedFileIds: string[];
    setSelectedFileIds: (v: string[]) => void;
}

export function StepContext({
    managedAgents,
    managedAgentIds,
    setManagedAgentIds,
    useMemory,
    setUseMemory,
    selectedFileIds,
    setSelectedFileIds
}: StepContextProps) {
    const [referenceFiles, setReferenceFiles] = useState<ReferenceFileItem[]>(
        []
    );
    const [loadingReferenceFiles, setLoadingReferenceFiles] = useState(false);
    const [uploadingReferenceFile, setUploadingReferenceFile] = useState(false);
    const referenceFileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        setLoadingReferenceFiles(true);
        fetch('/mgmt/api/v1/ClaudeFiles', { credentials: 'include' })
            .then((res) => (res.ok ? res.json() : []))
            .then((data) => setReferenceFiles(Array.isArray(data) ? data : []))
            .catch(() => setReferenceFiles([]))
            .finally(() => setLoadingReferenceFiles(false));
    }, []);

    const uploadReferenceFile = async (file: File) => {
        setUploadingReferenceFile(true);
        try {
            // 1. Upload the raw file to Anthropic's Files API (only the
            // chat-server backend talks to Anthropic directly).
            const formData = new FormData();
            formData.append('file', file);
            const uploadRes = await fetch('/api/reference-files', {
                method: 'POST',
                credentials: 'include',
                body: formData
            });
            if (!uploadRes.ok) return;
            const uploaded: { id: string; filename: string } =
                await uploadRes.json();

            // 2. Register it against this org in mgmt-API, so future listing
            // is a fast, org-scoped DB query instead of leaking cross-org.
            const created = await fetch('/mgmt/api/v1/ClaudeFiles', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    anthropicFileId: uploaded.id,
                    filename: uploaded.filename
                })
            }).then((res) => (res.ok ? res.json() : null));
            if (!created) return;

            setReferenceFiles((prev) => [...prev, created]);
            setSelectedFileIds([...selectedFileIds, created.id]);
        } finally {
            setUploadingReferenceFile(false);
        }
    };

    const toggleReferenceFile = (id: string) => {
        setSelectedFileIds(
            selectedFileIds.includes(id)
                ? selectedFileIds.filter((fid) => fid !== id)
                : [...selectedFileIds, id]
        );
    };

    // The orchestration coordinator runs behind the scenes — it's never a
    // pickable "expert" for a job. Picking more than one expert only makes
    // sense once the org has a coordinator registered (it's what actually
    // runs the job and delegates to the roster); without one, force
    // single-select so a job can't be saved in a state it can never run in.
    const pickableAgents = managedAgents.filter((a) => !a.isCoordinator);
    const hasCoordinator = managedAgents.some((a) => a.isCoordinator);

    const toggleAgent = (id: string) => {
        if (!hasCoordinator) {
            setManagedAgentIds([id]);
            return;
        }
        setManagedAgentIds(
            managedAgentIds.includes(id)
                ? managedAgentIds.filter((aid) => aid !== id)
                : [...managedAgentIds, id]
        );
    };

    return (
        <div className="space-y-12">
            <div className="space-y-6">
                <div className="">
                    <h2 className="mb-0 font-medium text-2xl">
                        Context &amp; tools
                    </h2>
                    <p className="text-muted-foreground text-base">
                        {hasCoordinator
                            ? 'Choose which agent(s) run this job.'
                            : 'Choose which agent runs this job.'}
                    </p>
                </div>

                <div>
                    <div className="mb-0 font-medium text-base">
                        Managed agent
                    </div>
                    <p className="mb-3 text-muted-foreground text-sm">
                        {hasCoordinator
                            ? 'Required — the job runs with the tools, data sources and instructions of the agent(s) you pick.'
                            : "Required — the job runs with the selected agent's tools, data sources and instructions."}
                    </p>
                    <ManagedAgentPicker
                        agents={pickableAgents}
                        selectedIds={managedAgentIds}
                        onToggle={toggleAgent}
                    />
                </div>
            </div>

            <div>
                <div className="mb-0 font-medium text-base">Memory store</div>
                <p className="mb-3 text-muted-foreground text-sm">
                    Optional — gives this job persistent memory across runs. Set
                    up automatically when you publish.
                </p>
                <div className="gap-3 grid grid-cols-2">
                    <button
                        type="button"
                        onClick={() => setUseMemory(!useMemory)}
                        className={cn(
                            'flex items-center gap-3 p-3.5 border rounded-xl text-left transition-all',
                            useMemory
                                ? 'border-foreground/40 bg-foreground/3'
                                : 'border-[#e9e8e4] dark:border-border bg-background'
                        )}
                        style={
                            useMemory
                                ? {
                                      borderColor: 'hsl(var(--foreground))',
                                      borderWidth: '1.5px'
                                  }
                                : {}
                        }
                    >
                        <div className="flex justify-center items-center bg-foreground rounded-xl size-[34px] text-background shrink-0">
                            <Brain className="size-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm truncate leading-tight">
                                Persistent memory
                            </div>
                            <div className="text-muted-foreground text-sm truncate">
                                Remember context across runs
                            </div>
                        </div>
                        <Switch
                            checked={useMemory}
                            size="default"
                            tabIndex={-1}
                            className="pointer-events-none"
                        />
                    </button>
                </div>
            </div>

            <div>
                <div className="mb-0 font-medium text-base">
                    Reference files
                </div>
                <p className="mb-3 text-muted-foreground text-sm">
                    Optional - read-only file(s) mounted into the sandbox on
                    every run (e.g. a template or fixed dataset).
                </p>
                <div className="gap-3 grid grid-cols-2">
                    {referenceFiles.map((file) => {
                        const selected = selectedFileIds.includes(file.id);
                        const FileTypeIcon = getReferenceFileIcon(
                            file.filename
                        );
                        return (
                            <button
                                key={file.id}
                                type="button"
                                onClick={() => toggleReferenceFile(file.id)}
                                className={cn(
                                    'flex items-center gap-3 p-3.5 border rounded-xl text-left transition-all',
                                    selected
                                        ? 'border-foreground/40 bg-foreground/3'
                                        : 'border-[#e9e8e4] dark:border-border bg-background'
                                )}
                                style={
                                    selected
                                        ? {
                                              borderColor:
                                                  'hsl(var(--foreground))',
                                              borderWidth: '1.5px'
                                          }
                                        : {}
                                }
                            >
                                <div className="flex justify-center items-center bg-foreground rounded-[0.625rem] size-[34px] text-background shrink-0">
                                    <FileTypeIcon className="size-4" />
                                </div>
                                <div className="flex-1 min-w-0 font-medium text-sm truncate">
                                    {file.filename}
                                </div>
                                <Switch
                                    checked={selected}
                                    size="default"
                                    tabIndex={-1}
                                    className="pointer-events-none"
                                />
                            </button>
                        );
                    })}

                    <button
                        type="button"
                        onClick={() => referenceFileInputRef.current?.click()}
                        disabled={uploadingReferenceFile}
                        className="flex items-center gap-3 hover:bg-accent p-3.5 border border-foreground/20 border-dashed rounded-xl text-muted-foreground hover:text-foreground text-left transition-all"
                    >
                        <div className="flex justify-center items-center border border-border rounded-lg size-10">
                            {uploadingReferenceFile ? (
                                <Loader2 className="size-4 animate-spin" />
                            ) : (
                                <Plus className="size-4" />
                            )}
                        </div>
                        <div className="font-medium text-sm">
                            Upload reference file
                        </div>
                    </button>
                    <input
                        ref={referenceFileInputRef}
                        type="file"
                        className="hidden"
                        onChange={(e) => {
                            const file = e.target.files?.[0];
                            e.target.value = '';
                            if (file) uploadReferenceFile(file);
                        }}
                    />
                </div>

                {loadingReferenceFiles && (
                    <p className="mt-2 text-[12px] text-muted-foreground">
                        Loading reference files…
                    </p>
                )}
            </div>
        </div>
    );
}
